import { ClipboardText } from '@phosphor-icons/react';
import { Badge } from '~/components/ui/badge';
import { usePlan } from '~/hooks/queries/use-plan';
import { planChipStatus, selectPlanStatus } from './plan-status';

interface ProposePlanOutput {
  status?: string;
  plan?: {
    id?: number;
    title?: string;
  };
}

interface ProposePlanCardProps {
  output: unknown;
  sessionId: string | undefined;
}

// FRONT-1412 review finding: `planChipStatus`/`selectPlanStatus` moved to
// `plan-status.ts` (dependency-free) so the phone surface can import this
// exact logic without pulling in this whole module — re-exported here so
// this file's own call site below and `propose-plan-card.test.ts` (which
// imports both from `'./propose-plan-card'`) are untouched.
export { planChipStatus, selectPlanStatus } from './plan-status';

/**
 * FRONT-755: the transcript-inline chip for a propose_plan tool call. This
 * is a compact, NON-interactive audit entry — the interactive approve/
 * reject/comment card is the separate floating `PlanCard` above the
 * composer (see plan-card.tsx / routes/home.tsx). This chip's only job is
 * to show the CURRENT status of the plan the tool call proposed, instead
 * of freezing forever at the "pending" status baked into the tool's output
 * at propose time.
 */
export function ProposePlanCard({ output, sessionId }: ProposePlanCardProps) {
  const parsed = (output ?? {}) as ProposePlanOutput;
  const frozenId = parsed.plan?.id;
  const title = parsed.plan?.title;

  const { data: livePlan } = usePlan(sessionId);

  const status = selectPlanStatus(livePlan, frozenId, parsed.status);
  const { label, variant } = planChipStatus(status);

  return (
    <div className="border border-border rounded-lg px-3 py-2 text-xs flex items-center gap-2">
      <ClipboardText size={14} className="text-muted-foreground shrink-0" />
      <span className="font-medium text-foreground shrink-0">Plan</span>
      {title && <span className="text-muted-foreground truncate min-w-0">{title}</span>}
      <Badge variant={variant} className="ml-auto">
        {label}
      </Badge>
    </div>
  );
}
